import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import CommandPalette from "@/components/CommandPalette";
import QuickTradeEntry from "@/components/QuickTradeEntry";
import {
  AlertTriangle,
  ArrowRightLeft,
  BarChart3,
  BookOpen,
  Calendar,
  FlaskConical,
  LayoutDashboard,
  Layers,
  List,
  LogOut,
  Menu,
  Plus,
  RadioTower,
  Scale,
  Settings,
  Trophy,
  Upload,
  X,
} from "lucide-react";
import { API_URL } from "@/lib/api";
import { formatDateToYMD } from "@/lib/date";
import {
  TRADING_MODES,
  getModeLabel,
  getStrategyStorageKey,
  isLiveModeConfirmed,
  setLiveModeConfirmed,
} from "@/lib/tradingMode";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = API_URL;

const navItems = [
  { suffix: "", label: "Dashboard", icon: LayoutDashboard },
  { suffix: "statistics", label: "Statistics", icon: BarChart3 },
  { suffix: "trades", label: "Trades", icon: List },
  { suffix: "calendar", label: "Calendar", icon: Calendar },
  { suffix: "comparison", label: "Compare", icon: Scale },
  { suffix: "import", label: "Import", icon: Upload },
  { suffix: "playbook", label: "Playbook", icon: BookOpen },
  { suffix: "portfolio", label: "Portfolio", icon: Layers },
  { suffix: "settings", label: "Strategie", icon: Settings },
];

const modeStyleMap = {
  [TRADING_MODES.BACKTESTING]: {
    badgeClass: "border-primary/40 bg-primary/15 text-primary",
    bannerClass: "border-primary/30 bg-primary/10 text-primary",
    switchClass:
      "border-primary/40 bg-card text-primary hover:bg-primary/10 hover:text-primary",
    icon: FlaskConical,
  },
  [TRADING_MODES.LIVE]: {
    badgeClass: "border-destructive/40 bg-destructive/15 text-destructive",
    bannerClass: "border-destructive/35 bg-destructive/10 text-destructive",
    switchClass:
      "border-destructive/40 bg-card text-destructive hover:bg-destructive/10 hover:text-destructive",
    icon: RadioTower,
  },
};

export default function DashboardLayout({
  user,
  onLogout,
  mode = TRADING_MODES.BACKTESTING,
  modeLabel = getModeLabel(mode),
  children,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState([]);
  const [currentStrategy, setCurrentStrategy] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newStrategy, setNewStrategy] = useState({
    name: "",
    currency: "USD",
    initial_capital: 10000,
  });
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [riskStatus, setRiskStatus] = useState(null);

  // Command palette keyboard shortcut
  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandPaletteOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const modeBasePath = `/${mode}`;
  const targetMode =
    mode === TRADING_MODES.BACKTESTING ? TRADING_MODES.LIVE : TRADING_MODES.BACKTESTING;
  const targetModeLabel = getModeLabel(targetMode);
  const strategyStorageKey = useMemo(() => getStrategyStorageKey(mode), [mode]);
  const modeStyles = modeStyleMap[mode] || modeStyleMap[TRADING_MODES.BACKTESTING];
  const ModeIcon = modeStyles.icon;

  const buildPath = (suffix = "") => (suffix ? `${modeBasePath}/${suffix}` : modeBasePath);

  const loadStrategies = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/strategies?mode=${mode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStrategies(response.data);

      const savedStrategyId = localStorage.getItem(strategyStorageKey);
      if (savedStrategyId) {
        const savedStrategy = response.data.find((strategy) => strategy.id === savedStrategyId);
        if (savedStrategy) {
          setCurrentStrategy(savedStrategy);
          return;
        }
      }

      if (response.data.length > 0) {
        setCurrentStrategy(response.data[0]);
        localStorage.setItem(strategyStorageKey, response.data[0].id);
      } else {
        setCurrentStrategy(null);
        localStorage.removeItem(strategyStorageKey);
      }
    } catch (error) {
      console.error("Error loading strategies:", error);
    }
  }, [mode, strategyStorageKey]);

  useEffect(() => {
    loadStrategies();
  }, [loadStrategies]);

  const loadRiskStatus = useCallback(async () => {
    if (!currentStrategy) {
      setRiskStatus(null);
      return;
    }
    try {
      const today = formatDateToYMD(new Date());
      const response = await axios.get(
        `${API}/risk-status/${currentStrategy.id}?mode=${mode}&today=${today}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setRiskStatus(response.data);
    } catch (error) {
      setRiskStatus(null);
    }
  }, [currentStrategy, mode]);

  useEffect(() => {
    loadRiskStatus();
  }, [loadRiskStatus, location.pathname]);

  const handleCreateStrategy = async (event) => {
    event.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/strategies`,
        { ...newStrategy, mode },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStrategies([...strategies, response.data]);
      setCurrentStrategy(response.data);
      localStorage.setItem(strategyStorageKey, response.data.id);
      setOpenDialog(false);
      setNewStrategy({ name: "", currency: "USD", initial_capital: 10000 });
      toast.success("Strategy creata con successo");
    } catch (error) {
      toast.error("Errore durante la creazione della strategy");
    }
  };

  const handleStrategyChange = (strategyId) => {
    const strategy = strategies.find((item) => item.id === strategyId);
    setCurrentStrategy(strategy || null);
    if (strategy) {
      localStorage.setItem(strategyStorageKey, strategyId);
    }
    setMobileMenuOpen(false);
  };

  const getModeSwitchPath = () => {
    const currentPrefix = `/${mode}`;
    const targetPrefix = `/${targetMode}`;

    if (location.pathname === currentPrefix) {
      return targetPrefix;
    }

    if (location.pathname.startsWith(`${currentPrefix}/`)) {
      return location.pathname.replace(currentPrefix, targetPrefix);
    }

    return targetPrefix;
  };

  const handleModeSwitch = () => {
    if (targetMode === TRADING_MODES.LIVE && !isLiveModeConfirmed()) {
      const confirmed = window.confirm(
        "Stai entrando in Live Trading. Le azioni avranno effetto reale. Vuoi continuare?"
      );
      if (!confirmed) {
        return;
      }
      setLiveModeConfirmed();
    }

    setMobileMenuOpen(false);
    navigate(getModeSwitchPath());
  };

  return (
    <div className="flex h-screen bg-background">
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between border-b border-border bg-card/95 p-4 shadow-lg backdrop-blur-md md:hidden">
        <Link to={buildPath()} onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2">
          <h1 className="text-xl font-bold tracking-tight bg-gradient-to-r from-primary to-orange-500 bg-clip-text text-transparent">
            TRADING TRACK
          </h1>
          <Badge className={modeStyles.badgeClass}>{modeLabel}</Badge>
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
          className="hover:bg-primary/10"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      <aside
        className={`fixed md:static inset-y-0 left-0 z-40 w-64 flex-col border-r border-border bg-card/98 shadow-xl backdrop-blur-md transition-transform duration-200 ease-in-out md:flex ${
          mobileMenuOpen ? "flex translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="hidden h-16 items-center justify-between border-b border-border bg-card px-6 md:flex">
          <Link to={buildPath()} onClick={() => setMobileMenuOpen(false)}>
            <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-primary to-orange-500 bg-clip-text text-transparent">
              TRADING TRACK
            </h1>
          </Link>
          <Badge className={modeStyles.badgeClass}>{modeLabel}</Badge>
        </div>

        <div className="mt-16 flex-1 overflow-y-auto p-4 md:mt-0">
          <div className={`mb-4 flex items-center gap-2 rounded-lg border px-3 py-2 ${modeStyles.bannerClass}`}>
            <ModeIcon className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase">Modalita attiva: {modeLabel}</span>
          </div>

          <div className="mb-6 space-y-2">
            <Label className="text-xs font-semibold uppercase text-white/70">Active Strategy</Label>
            <div className="space-y-2">
              {strategies.map((strategy) => (
                <button
                  key={strategy.id}
                  onClick={() => handleStrategyChange(strategy.id)}
                  className={`w-full rounded-xl border px-4 py-3 text-left transition-all duration-200 ${
                    currentStrategy?.id === strategy.id
                      ? "border-primary bg-gradient-to-r from-primary to-orange-600 text-white shadow-lg"
                      : "border-border text-white hover:border-primary/40 hover:bg-primary/10"
                  }`}
                  data-testid={`strategy-option-${strategy.id}`}
                >
                  <div className="font-semibold">{strategy.name}</div>
                  <div className="text-xs opacity-80">{strategy.currency}</div>
                </button>
              ))}
            </div>

            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-primary/40 hover:border-primary hover:bg-primary/10"
                  data-testid="create-strategy-button"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Strategy
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="create-strategy-dialog">
                <DialogHeader>
                  <DialogTitle>Create New Strategy</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateStrategy} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="strategy-name">Strategy Name</Label>
                    <Input
                      id="strategy-name"
                      data-testid="strategy-name-input"
                      value={newStrategy.name}
                      onChange={(event) =>
                        setNewStrategy({ ...newStrategy, name: event.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="strategy-currency">Currency</Label>
                    <Input
                      id="strategy-currency"
                      data-testid="strategy-currency-input"
                      value={newStrategy.currency}
                      onChange={(event) =>
                        setNewStrategy({
                          ...newStrategy,
                          currency: event.target.value.toUpperCase(),
                        })
                      }
                      placeholder="USD, EUR, GBP..."
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="initial-capital">Initial Capital</Label>
                    <Input
                      id="initial-capital"
                      data-testid="initial-capital-input"
                      type="number"
                      step="0.01"
                      value={newStrategy.initial_capital}
                      onChange={(event) =>
                        setNewStrategy({
                          ...newStrategy,
                          initial_capital: parseFloat(event.target.value),
                        })
                      }
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full rounded-full" data-testid="submit-strategy-button">
                    Create Strategy
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <nav className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const itemPath = buildPath(item.suffix);
              const isActive = location.pathname === itemPath;
              return (
                <Link key={itemPath} to={itemPath} onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    className={`w-full justify-start text-white transition-all duration-200 ${
                      isActive
                        ? "border border-primary/40 bg-primary/15 shadow-sm"
                        : "hover:bg-primary/10 hover:text-white"
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="border-t border-border bg-card p-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleModeSwitch}
            className={`mb-4 w-full justify-start rounded-full border ${modeStyles.switchClass}`}
            data-testid="mode-switch-button"
          >
            <ArrowRightLeft className="h-4 w-4" />
            <span>Passa a {targetModeLabel}</span>
          </Button>

          <div className="mb-3 flex items-center space-x-3 px-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-orange-600 font-semibold text-primary-foreground shadow-lg">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="truncate text-sm font-medium text-foreground">{user.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={onLogout}
            data-testid="logout-button"
          >
            <LogOut className="mr-3 h-5 w-5" />
            Logout
          </Button>
        </div>
      </aside>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <CommandPalette
        mode={mode}
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
      />

      <main className="flex-1 overflow-y-auto pt-16 md:pt-0">
        {currentStrategy && riskStatus?.daily_loss_breached && (
          <div
            className="mx-4 mt-4 flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive md:mx-6"
            data-testid="risk-banner-loss"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              Daily loss limit reached: {riskStatus.today_pnl} {currentStrategy.currency} today
              {riskStatus.daily_max_loss ? ` (limit ${riskStatus.daily_max_loss})` : ""}. Stop trading for today.
            </span>
          </div>
        )}
        {currentStrategy && riskStatus?.weekly_target_reached && (
          <div
            className="mx-4 mt-4 flex items-center gap-2 rounded-lg border border-success/40 bg-success/10 px-4 py-2 text-sm text-success md:mx-6"
            data-testid="risk-banner-target"
          >
            <Trophy className="h-4 w-4 shrink-0" />
            <span>
              Weekly target reached: {riskStatus.week_pnl} {currentStrategy.currency} this week.
            </span>
          </div>
        )}
        {currentStrategy ? (
          React.cloneElement(children, { currentStrategy, mode })
        ) : (
          <div className="flex h-full items-center justify-center p-4" data-testid="no-strategy-message">
            <div className="text-center">
              <h2 className="mb-2 text-2xl font-bold">No Strategy</h2>
              <p className="mb-4 text-muted-foreground">Create a strategy to get started</p>
              <Button
                onClick={() => setOpenDialog(true)}
                className="rounded-full"
                data-testid="create-first-strategy-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create First Strategy
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Quick Trade Entry FAB - visible on all pages */}
      <QuickTradeEntry
        currentStrategy={currentStrategy}
        mode={mode}
      />
    </div>
  );
}
